import { defineComponent, h } from 'vue'
import {
  createRouter,
  createWebHistory,
  type NavigationGuardNext,
  type RouteLocationNormalized,
  type RouteRecordRaw
} from 'vue-router'
import LoginView from '../views/LoginView.vue'
import SystemLoginView from '../views/SystemLoginView.vue'
import LandingView from '../views/LandingView.vue'
import RegisterProjectView from '../views/RegisterProjectView.vue'
import ForgotPasswordView from '../views/ForgotPasswordView.vue'
import DashboardView from '../views/DashboardView.vue'
import SafetySubmissionsView from '../views/SafetySubmissionsView.vue'
import SafetyCreateView from '../views/SafetyCreateView.vue'
import SafetyDialogFormView from '../views/SafetyDialogFormView.vue'
import SafetyOccurrenceFormView from '../views/SafetyOccurrenceFormView.vue'
import SafetyDocumentView from '../views/SafetyDocumentView.vue'
import SubmissionsView from '../views/SubmissionsView.vue'
import SubmissionFormView from '../views/SubmissionFormView.vue'
import SubmissionView from '../views/SubmissionView.vue'
import AdminReportsView from '../views/AdminReportsView.vue'
import AdminLoginView from '../views/AdminLoginView.vue'
import AdminDashboardView from '../views/AdminDashboardView.vue'
import AdminSystemView from '../views/AdminSystemView.vue'
import AdminSysOverviewTab from '../views/admin-system/AdminSysOverviewTab.vue'
import AdminSysUsersTab from '../views/admin-system/AdminSysUsersTab.vue'
import AdminSysProjectsTab from '../views/admin-system/AdminSysProjectsTab.vue'
import AdminSysDataTab from '../views/admin-system/AdminSysDataTab.vue'
import AdminSysConfigTab from '../views/admin-system/AdminSysConfigTab.vue'
import AdminSysPaymentTab from '../views/admin-system/AdminSysPaymentTab.vue'
import AdminSysNotesTab from '../views/admin-system/AdminSysNotesTab.vue'
import AdminSysAnnouncementsTab from '../views/admin-system/AdminSysAnnouncementsTab.vue'
import AdminSysSupportTab from '../views/admin-system/AdminSysSupportTab.vue'
import AdminSysFlagsTab from '../views/admin-system/AdminSysFlagsTab.vue'
import AdminSysIATab from '../views/admin-system/AdminSysIATab.vue'
import AdminSysLogsTab from '../views/admin-system/AdminSysLogsTab.vue'
import SuspendedView from '../views/SuspendedView.vue'
import ResetPasswordView from '../views/ResetPasswordView.vue'
import AcceptInviteView from '../views/AcceptInviteView.vue'
import GanttPublicView from '../views/GanttPublicView.vue'
import QuizPublicView from '../views/QuizPublicView.vue'
import IAView from '../views/IAView.vue'
import { sessionStore } from '../store/session'
import { masterSessionStore } from '../store/masterSession'
import { navigationStore } from '../store/navigation'
import { getSubdomainUsername, isOnSubdomain, subdomainLoginUrl, isMasterContext, isMasterSubdomain } from '../utils/subdomain'

// Enforcement do subdomínio dedicado do painel /administrador.
// Só liga quando VUE_APP_ENFORCE_MASTER_SUBDOMAIN === '1' — assim o código pode
// ser publicado ANTES de configurar o DNS (valdisnei.obratycoon.com.br) sem
// trancar o acesso ao painel. Ative a flag depois que o subdomínio estiver no ar.
const ENFORCE_MASTER_SUBDOMAIN = process.env.VUE_APP_ENFORCE_MASTER_SUBDOMAIN === '1'

// No subdomínio → SystemLoginView (lê username do host)
// No domínio principal → LoginView genérico
const SmartLoginView = defineComponent({
  name: 'SmartLoginView',
  setup: () => () => h(isOnSubdomain() ? SystemLoginView : LoginView)
})

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'root',
    redirect: () => {
      // Subdomínio dedicado do painel: raiz vai direto pro /administrador
      if (isMasterSubdomain()) return '/administrador'
      if (sessionStore.isLoggedIn.value) return '/home'
      const sub = getSubdomainUsername()
      if (sub) return `/login/${encodeURIComponent(sub)}`
      return '/apresentacao'
    },
    meta: { public: true }
  },
  {
    path: '/apresentacao',
    name: 'landing',
    component: LandingView,
    meta: { public: true }
  },
  {
    path: '/planos',
    name: 'planos',
    component: () => import('../views/PlanosView.vue'),
    meta: { public: true }
  },
  {
    path: '/saiba-mais',
    name: 'saiba-mais',
    component: () => import('../views/SaibaMaisView.vue'),
    meta: { public: true }
  },
  {
    path: '/registro',
    name: 'registro',
    component: () => import('../views/RegisterAccountView.vue'),
    meta: { public: true }
  },
  {
    path: '/termos',
    name: 'termos',
    component: () => import('../views/TermsView.vue'),
    meta: { public: true }
  },
  {
    path: '/privacidade',
    name: 'privacidade',
    component: () => import('../views/PrivacyView.vue'),
    meta: { public: true }
  },
  {
    path: '/entrar',
    name: 'entrar',
    component: () => import('../views/LoginAccountView.vue'),
    meta: { public: true }
  },
  {
    path: '/home',
    name: 'home',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/meus-relatorios',
    name: 'my-reports',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/visao-geral',
    name: 'overview',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/consulta-documentos',
    name: 'documents',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/desenhos',
    name: 'drawings',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/gerenciar-projetos',
    name: 'manage-projects',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/gerenciar-usuarios',
    name: 'manage-users',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/meus-projetos',
    name: 'my-projects',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/minhas-tarefas',
    name: 'my-tasks',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/gerenciar-sistema',
    name: 'manage-system',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/profile',
    name: 'profile',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/profile/editar',
    name: 'profile-editar',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/profile/seguranca',
    name: 'profile-seguranca',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/profile/email',
    name: 'profile-email',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/profile/aparencia',
    name: 'profile-aparencia',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/profile/offline',
    name: 'profile-offline',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/profile/notificacoes',
    name: 'profile-notificacoes',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/profile/versoes',
    name: 'profile-versoes',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/profile/fuso',
    name: 'profile-fuso',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/login',
    name: 'login',
    component: SmartLoginView,
    meta: { public: true }
  },
  {
    path: '/login/:systemUsername',
    name: 'login-system',
    component: SystemLoginView,
    meta: { public: true }
  },
  {
    path: '/register',
    name: 'register-project',
    component: RegisterProjectView,
    meta: { public: true }
  },
  {
    path: '/forgot',
    name: 'forgot',
    component: ForgotPasswordView,
    meta: { public: true }
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: ResetPasswordView,
    meta: { public: true }
  },
  {
    path: '/accept-invite',
    name: 'accept-invite',
    component: AcceptInviteView,
    meta: { public: true }
  },
  {
    path: '/submissions',
    name: 'submissions',
    component: SubmissionsView,
    meta: { requiresAuth: true }
  },
  {
    path: '/safety',
    name: 'safety',
    component: SafetySubmissionsView,
    meta: { requiresAuth: true }
  },
  {
    path: '/safety/new',
    name: 'safety-new',
    component: SafetyCreateView,
    meta: { requiresAuth: true }
  },
  {
    path: '/safety/dialogo',
    name: 'safety-dialogo',
    component: SafetyDialogFormView,
    meta: { requiresAuth: true }
  },
  {
    path: '/safety/dialogo/:id',
    name: 'safety-dialogo-edit',
    component: SafetyDialogFormView,
    meta: { requiresAuth: true }
  },
  {
    path: '/safety/ocorrido',
    name: 'safety-ocorrido',
    component: SafetyOccurrenceFormView,
    meta: { requiresAuth: true }
  },
  {
    path: '/safety/ocorrido/:id',
    name: 'safety-ocorrido-edit',
    component: SafetyOccurrenceFormView,
    meta: { requiresAuth: true }
  },
  {
    path: '/safety/:id',
    name: 'safety-view',
    component: SafetyDocumentView,
    meta: { requiresAuth: true }
  },
  {
    path: '/submission/new',
    name: 'submission-new',
    component: SubmissionFormView,
    meta: { requiresAuth: true }
  },
  {
    path: '/submission/:id',
    name: 'submission-view',
    component: SubmissionView,
    meta: { requiresAuth: true }
  },
  {
    path: '/submission/:id/edit',
    name: 'submission-edit',
    component: SubmissionFormView,
    meta: { requiresAuth: true }
  },
  {
    path: '/geral',
    name: 'geral',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/admin',
    name: 'admin-mode',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/admin/:pathMatch(.*)*',
    name: 'admin-mode-sub',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/geral/:pathMatch(.*)*',
    name: 'geral-sub',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/administrador/reports',
    name: 'admin-reports',
    component: AdminReportsView,
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/master-login',
    redirect: '/administrador'
  },
  {
    // Atalho do subdomínio dedicado (ex.: localhost/valdisnei em dev)
    path: '/valdisnei',
    redirect: '/administrador'
  },
  {
    path: '/admin/reports',
    redirect: '/administrador/reports'
  },
  {
    // Dashboard legado removido — redireciona bookmarks para o painel atual
    path: '/master',
    redirect: '/administrador/dashboard'
  },
  {
    path: '/administrador',
    name: 'admin-login',
    component: AdminLoginView,
    meta: { public: true }
  },
  {
    path: '/administrador/dashboard',
    name: 'admin-dashboard',
    component: AdminDashboardView,
    meta: { requiresMaster: true }
  },
  {
    path: '/administrador/systems/:systemId',
    component: AdminSystemView,
    meta: { requiresMaster: true },
    children: [
      { path: '', redirect: (to: RouteLocationNormalized) => `/administrador/systems/${to.params.systemId}/visao-geral` },
      { path: 'visao-geral', name: 'admin-sys-overview', component: AdminSysOverviewTab },
      { path: 'usuarios', name: 'admin-sys-users', component: AdminSysUsersTab },
      { path: 'projetos', name: 'admin-sys-projects', component: AdminSysProjectsTab },
      { path: 'dados', name: 'admin-sys-data', component: AdminSysDataTab },
      { path: 'configuracoes', name: 'admin-sys-config', component: AdminSysConfigTab },
      { path: 'pagamento', name: 'admin-sys-payment', component: AdminSysPaymentTab },
      { path: 'notas', name: 'admin-sys-notes', component: AdminSysNotesTab },
      { path: 'anuncios', name: 'admin-sys-announcements', component: AdminSysAnnouncementsTab },
      { path: 'suporte', name: 'admin-sys-support', component: AdminSysSupportTab },
      { path: 'modulos', name: 'admin-sys-flags', component: AdminSysFlagsTab },
      { path: 'ia', name: 'admin-sys-ia', component: AdminSysIATab },
      { path: 'logs', name: 'admin-sys-logs', component: AdminSysLogsTab },
    ]
  },
  {
    path: '/:systemUsername/projeto/:projectSlug',
    name: 'project-workspace',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/:systemUsername/projeto/:projectSlug/:workspaceTab',
    name: 'project-workspace-tab',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/project/:projectSlug',
    redirect: '/home'
  },
  {
    path: '/ia',
    name: 'ia-chat',
    component: IAView,
    meta: { requiresAuth: true }
  },
  {
    path: '/anotacoes',
    name: 'anotacoes',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/notificacoes',
    name: 'notificacoes',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/almoxarifado',
    name: 'warehouse',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/almoxarifado/fornecer',
    name: 'warehouse-provide',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/almoxarifado/adicionar-item',
    name: 'warehouse-add-item',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/almoxarifado/avarias',
    name: 'warehouse-damages',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/almoxarifado/avarias/nova',
    name: 'warehouse-damage-new',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/almoxarifado/avarias/detalhe/:id',
    name: 'warehouse-damage-detail',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/almoxarifado/transferir-item',
    name: 'warehouse-transfer-item',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/almoxarifado/solicitar-item',
    name: 'warehouse-request-item',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/almoxarifado/importar-nf',
    name: 'warehouse-nfe-import',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/escritorio',
    name: 'office',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/escritorio/questionario',
    name: 'office-quizzes',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/manutencao',
    name: 'maintenance',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/manutencao/:projectId',
    name: 'maintenance-project',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/preencher-os/:osId',
    name: 'preencher-os',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/portaria',
    name: 'portaria',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/maquinario-pesado',
    name: 'heavy-machinery',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/chamados',
    name: 'tickets',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/log-atividades',
    name: 'activity-log',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/suspended',
    name: 'suspended',
    component: SuspendedView,
    meta: { public: true }
  },
  {
    path: '/gantt/:hash',
    name: 'gantt-public',
    component: GanttPublicView,
    meta: { public: true }
  },
  {
    path: '/questionario/:hash',
    name: 'quiz-public',
    component: QuizPublicView,
    meta: { public: true }
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: () => {
      if (sessionStore.isLoggedIn.value) return '/home'
      const sub = getSubdomainUsername()
      if (sub) return `/login/${encodeURIComponent(sub)}`
      return '/apresentacao'
    }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

router.beforeEach((to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
  // ── Painel /administrador restrito ao subdomínio dedicado (valdisnei) ──────
  const goingToAdmin = to.path === '/administrador' || to.path.startsWith('/administrador/')
  if (goingToAdmin && ENFORCE_MASTER_SUBDOMAIN && !isMasterContext()) {
    // Fora do subdomínio master (e fora do localhost): painel fica oculto.
    window.location.href = 'https://obratycoon.com.br'
    return
  }
  // No subdomínio dedicado do painel (valdisnei), só existe o /administrador:
  // qualquer outra rota (raiz, /login/valdisnei tratando como obra, etc.) é
  // desviada pro painel em vez de tentar carregar uma obra inexistente.
  if (isMasterSubdomain() && !goingToAdmin) {
    next('/administrador')
    return
  }

  // Regras para subdomínios (ex: minhaconstrutora.obratycoon.com.br)
  if (isOnSubdomain()) {
    // /login/:qualquercoisa → /login (subdomain já carrega o contexto)
    if (to.name === 'login-system') {
      next('/login')
      return
    }
    // /register → obratycoon.com.br/register
    if (to.name === 'register-project') {
      window.location.href = 'https://obratycoon.com.br/register'
      return
    }
  }

  // No domínio principal: /login/:systemUsername → redireciona para subdomínio em produção
  if (!isOnSubdomain() && to.name === 'login-system') {
    const paramUsername = typeof to.params.systemUsername === 'string' ? to.params.systemUsername : ''
    if (paramUsername) {
      const url = subdomainLoginUrl(paramUsername)
      if (url.startsWith('http')) {
        window.location.href = url
        return
      }
      // localhost: renderiza normalmente
    }
  }

  if (to.meta.public) {
    next()
    return
  }

  if (to.meta.requiresAuth && !sessionStore.isLoggedIn.value) {
    // On a subdomain, the login page needs no URL param — the subdomain carries context
    if (isOnSubdomain()) {
      const sub = getSubdomainUsername()
      next(`/login/${encodeURIComponent(sub)}`)
      return
    }
    // On main domain: try to preserve systemUsername from params or session
    const paramUsername = typeof to.params.systemUsername === 'string' ? to.params.systemUsername : ''
    const sessionUsername = sessionStore.state.user?.systemUsername || ''
    const username = paramUsername || sessionUsername
    if (username) {
      const url = subdomainLoginUrl(username)
      if (url.startsWith('http')) {
        window.location.href = url
        return
      }
      next(`/login/${encodeURIComponent(username)}`)
      return
    }
    next('/login')
    return
  }

  if (to.meta.requiresAdmin && !sessionStore.isAdmin.value) {
    next('/submissions')
    return
  }
  if (to.meta.requiresMaster && !masterSessionStore.isLoggedIn.value) {
    next('/administrador')
    return
  }
  next()
})

router.afterEach((to) => {
  navigationStore.push(to.fullPath)
})

export default router
